import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { Task, TaskPriority, TaskStatus, RecurrencePattern } from '@/types/lead';
import { Json } from '@/integrations/supabase/types';

// Helper to convert date to ISO string or null
const toIsoOrNull = (d: unknown): string | null => {
  if (!d) return null;
  try {
    if (typeof d === 'string') return new Date(d).toISOString();
    return (d as Date).toISOString();
  } catch {
    return null;
  }
};

// Create a new task
export const createTask = async (task: Omit<Task, 'id' | 'createdAt'>): Promise<Task> => {
  // Get current user to set as fallback for assigned_to
  const { data: authData } = await supabase.auth.getUser();
  const fallbackAssignedTo = task.assignedTo || authData.user?.id || null;
  
  try {
    const newTask = {
      lead_id: task.leadId,
      title: task.title,
      description: task.description,
      due_date: toIsoOrNull(task.dueDate),
      priority: task.priority,
      status: task.status,
      assigned_to: fallbackAssignedTo,
      completed_at: task.completedAt || null,
      categories: task.categories && task.categories.length > 0 ? task.categories : null,
      reminder_times: task.reminderTimes && task.reminderTimes.length > 0 ? task.reminderTimes : null,
      checklist: task.checklist && task.checklist.length > 0 ? (task.checklist as unknown as Json) : null,
      recurrence_pattern: task.recurrencePattern || null,
      recurrence_interval: task.recurrenceInterval || 1,
      recurrence_end_date: toIsoOrNull(task.recurrenceEndDate),
      parent_task_id: task.parentTaskId || null,
    };

    console.log('[TaskService] Inserting task payload:', newTask);

    const { data, error } = await supabase
      .from('tasks')
      .insert([newTask])
      .select('*')
      .single();

    if (error) {
      console.error('[TaskService] Supabase insert error:', { 
        code: (error as any)?.code, 
        message: (error as any)?.message, 
        details: (error as any)?.details, 
        hint: (error as any)?.hint 
      });
      throw error;
    }

    return mapDbTaskToTask(data);
  } catch (error) {
    console.error('Error creating task:', error);
    // Fallback for local development only
    if (import.meta.env.DEV) {
      const createdAt = new Date().toISOString();
      const newTask: Task = {
        id: uuidv4(),
        leadId: task.leadId,
        title: task.title,
        description: task.description,
        dueDate: task.dueDate,
        priority: task.priority,
        status: task.status,
        assignedTo: task.assignedTo,
        createdAt,
        completedAt: task.completedAt,
        categories: task.categories,
        reminderTimes: task.reminderTimes,
      };
      
      const tasksJson = localStorage.getItem('tasks') || '[]';
      const tasks = JSON.parse(tasksJson);
      tasks.push(newTask);
      localStorage.setItem('tasks', JSON.stringify(tasks));
      
      return newTask;
    }
    throw error;
  }
};

// Update a task
export const updateTask = async (taskId: string, updates: Partial<Task>): Promise<Task> => {
  console.log('Updating task with ID:', taskId); // Add this line
  try {
    const mappedUpdates: any = {};
    
    if ('leadId' in updates) mappedUpdates.lead_id = updates.leadId;
    if ('title' in updates) mappedUpdates.title = updates.title;
    if ('description' in updates) mappedUpdates.description = updates.description;
    if ('dueDate' in updates && updates.dueDate) {
      mappedUpdates.due_date = typeof updates.dueDate === 'string' 
        ? updates.dueDate 
        : updates.dueDate.toISOString();
    }
    if ('priority' in updates) mappedUpdates.priority = updates.priority;
    if ('status' in updates) mappedUpdates.status = updates.status;
    if ('assignedTo' in updates) mappedUpdates.assigned_to = updates.assignedTo;
    if ('completedAt' in updates && updates.completedAt) mappedUpdates.completed_at = updates.completedAt;
    if ('categories' in updates) mappedUpdates.categories = updates.categories;
    if ('reminderTimes' in updates) mappedUpdates.reminder_times = updates.reminderTimes ? updates.reminderTimes : null;
    if ('checklist' in updates) mappedUpdates.checklist = updates.checklist && updates.checklist.length > 0 ? (updates.checklist as unknown as Json) : null;
    if ('recurrencePattern' in updates) mappedUpdates.recurrence_pattern = updates.recurrencePattern || null;
    if ('recurrenceInterval' in updates) mappedUpdates.recurrence_interval = updates.recurrenceInterval || 1;
    if ('recurrenceEndDate' in updates) mappedUpdates.recurrence_end_date = updates.recurrenceEndDate || null;
    if ('parentTaskId' in updates) mappedUpdates.parent_task_id = updates.parentTaskId || null;
    
    const { data, error } = await supabase
      .from('tasks')
      .update(mappedUpdates)
      .eq('id', taskId)
      .select()
      .single();

    if (error) throw error;

    return mapDbTaskToTask(data);
  } catch (error) {
    console.error('Error updating task:', error);
    
    // Fallback for local development
    const tasksJson = localStorage.getItem('tasks') || '[]';
    const tasks = JSON.parse(tasksJson);
    const index = tasks.findIndex((t: any) => t.id === taskId);
    
    if (index !== -1) {
      tasks[index] = { ...tasks[index], ...updates };
      localStorage.setItem('tasks', JSON.stringify(tasks));
      return tasks[index];
    }
    
    throw new Error('Task not found');
  }
};

// Delete a task
export const deleteTask = async (taskId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting task:', error);
    
    // Fallback for local development
    const tasksJson = localStorage.getItem('tasks') || '[]';
    const tasks = JSON.parse(tasksJson);
    const filteredTasks = tasks.filter((t: any) => t.id !== taskId);
    localStorage.setItem('tasks', JSON.stringify(filteredTasks));
  }
};

// Get tasks for multiple leads in a single query (batch fetch)
export const getTasksByLeads = async (leadIds: string[]): Promise<Record<string, Task[]>> => {
  try {
    if (leadIds.length === 0) {
      return {};
    }

    console.log('[TaskService] Batch fetching tasks for leads:', leadIds);
    
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .in('lead_id', leadIds)
      .order('due_date', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[TaskService] Batch fetch error:', error);
      throw error;
    }

    // Group tasks by lead_id
    const tasksByLead: Record<string, Task[]> = {};
    (data || []).forEach((dbTask: any) => {
      const task = mapDbTaskToTask(dbTask);
      if (!tasksByLead[task.leadId]) {
        tasksByLead[task.leadId] = [];
      }
      tasksByLead[task.leadId].push(task);
    });

    console.log('[TaskService] Batch fetched tasks grouped by lead:', Object.keys(tasksByLead).length, 'leads');
    
    return tasksByLead;
  } catch (error) {
    console.error('[TaskService] Error batch fetching tasks:', error);
    return {};
  }
};

// Get tasks for a lead
export const getTasksByLead = async (leadId: string): Promise<Task[]> => {
  try {
    // Defensive check: ensure leadId is a valid string
    if (!leadId || typeof leadId !== 'string' || leadId === 'undefined') {
      console.warn('[TaskService] Invalid leadId provided:', leadId);
      return [];
    }

    console.log('[TaskService] Fetching tasks for lead:', leadId);
    
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('lead_id', leadId)
      .order('due_date', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[TaskService] Error from Supabase:', error);
      throw error;
    }

    console.log('[TaskService] Raw data from Supabase:', data);
    console.log('[TaskService] Number of tasks:', data?.length || 0);
    
    const mappedTasks = (data || []).map(mapDbTaskToTask);
    console.log('[TaskService] Mapped tasks:', mappedTasks);
    
    return mappedTasks;
  } catch (error) {
    console.error('[TaskService] Error fetching tasks for lead:', error);
    
    // Fallback for local development only
    if (import.meta.env.DEV) {
      const tasksJson = localStorage.getItem('tasks') || '[]';
      const tasks = JSON.parse(tasksJson);
      return tasks
        .filter((t: any) => t.leadId === leadId)
        .map((t: any) => ({
          ...t,
          dueDate: t.dueDate ? new Date(t.dueDate) : undefined,
          completedAt: t.completedAt ? t.completedAt : undefined,
          categories: t.categories || [],
          reminderTimes: t.reminderTimes || [],
        }));
    }
    throw error;
  }
};

// Complete a task
export const completeTask = async (taskId: string): Promise<Task> => {
  return updateTask(taskId, {
    status: 'completed',
    completedAt: new Date().toISOString(),
  });
};

// Get task priority options with display information
export const getTaskPriorityOptions = () => {
  return [
    { value: 'low', label: 'Low', color: 'bg-blue-500' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
    { value: 'high', label: 'High', color: 'bg-red-500' },
  ];
};

// Get available task categories
export const getTaskCategoryOptions = () => {
  return [
    { value: 'follow-up', label: 'Follow-up' },
    { value: 'research', label: 'Research' },
    { value: 'meeting-prep', label: 'Meeting Prep' },
    { value: 'outreach', label: 'Outreach' },
    { value: 'documentation', label: 'Documentation' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'onboarding', label: 'Onboarding' },
  ];
};

// Helper function to map database task to frontend Task type
const mapDbTaskToTask = (dbTask: any): Task => {
  // Parse reminder_times if it exists
  let reminderTimes: string[] = [];
  if (dbTask.reminder_times) {
    try {
      if (typeof dbTask.reminder_times === 'string') {
        reminderTimes = JSON.parse(dbTask.reminder_times);
      } else if (Array.isArray(dbTask.reminder_times)) {
        reminderTimes = dbTask.reminder_times;
      }
    } catch (e) {
      console.error('Error parsing reminder_times:', e);
    }
  }
  
  return {
    id: dbTask.id,
    leadId: dbTask.lead_id,
    title: dbTask.title,
    description: dbTask.description,
    dueDate: dbTask.due_date ? new Date(dbTask.due_date) : undefined,
    priority: dbTask.priority as TaskPriority,
    status: dbTask.status as TaskStatus,
    assignedTo: dbTask.assigned_to,
    createdAt: dbTask.created_at,
    completedAt: dbTask.completed_at || undefined,
    categories: dbTask.categories || [],
    reminderTimes: reminderTimes,
    lastNotificationSent: dbTask.last_notification_sent,
    checklist: dbTask.checklist || undefined,
    recurrencePattern: dbTask.recurrence_pattern as RecurrencePattern | null || null,
    recurrenceInterval: dbTask.recurrence_interval || 1,
    recurrenceEndDate: dbTask.recurrence_end_date || null,
    parentTaskId: dbTask.parent_task_id || null,
  };
};

// Create next recurrence instance when a recurring task is completed
export const createNextRecurrence = async (completedTask: Task): Promise<Task | null> => {
  if (!completedTask.recurrencePattern || !completedTask.dueDate) return null;

  const currentDue = new Date(completedTask.dueDate);
  const interval = completedTask.recurrenceInterval || 1;

  let nextDue: Date;
  switch (completedTask.recurrencePattern) {
    case 'daily':
      nextDue = new Date(currentDue);
      nextDue.setDate(nextDue.getDate() + interval);
      break;
    case 'weekly':
      nextDue = new Date(currentDue);
      nextDue.setDate(nextDue.getDate() + 7 * interval);
      break;
    case 'monthly':
      nextDue = new Date(currentDue);
      nextDue.setMonth(nextDue.getMonth() + interval);
      break;
    case 'yearly':
      nextDue = new Date(currentDue);
      nextDue.setFullYear(nextDue.getFullYear() + interval);
      break;
    default:
      return null;
  }

  // Check if past end date
  if (completedTask.recurrenceEndDate && nextDue > new Date(completedTask.recurrenceEndDate)) {
    console.log('[TaskService] Recurrence end date reached, not creating next instance');
    return null;
  }

  // Clone the task with new due date
  return createTask({
    leadId: completedTask.leadId,
    title: completedTask.title,
    description: completedTask.description,
    dueDate: nextDue.toISOString(),
    priority: completedTask.priority,
    status: 'pending',
    assignedTo: completedTask.assignedTo,
    categories: completedTask.categories,
    reminderTimes: completedTask.reminderTimes,
    checklist: completedTask.checklist?.map(item => ({ ...item, completed: false })),
    recurrencePattern: completedTask.recurrencePattern,
    recurrenceInterval: completedTask.recurrenceInterval,
    recurrenceEndDate: completedTask.recurrenceEndDate,
    parentTaskId: completedTask.parentTaskId || completedTask.id,
  });
};
